import { useState } from 'react';
import { Plus, Trash2, Pencil, Clock, Star } from 'lucide-react';
import { useSlaPolicies, SlaPolicy } from '../../hooks/useSlaPolicies';
import { Card, Modal, Select, DataTable, EmptyState } from '../../components/ui';
import type { Column } from '../../components/ui';
import Button from '../../components/Button';

const PRIORITY_OPTS = [{ value: '', label: 'Any priority' },
  ...['Critical', 'High', 'Medium', 'Low'].map(p => ({ value: p, label: p }))];

function Editor({ initial, onClose, onSave }: {
  initial?: SlaPolicy; onClose: () => void; onSave: (d: Partial<SlaPolicy>) => Promise<boolean>;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [priority, setPriority] = useState(initial?.priority ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [resolutionHours, setResolutionHours] = useState(String(initial?.resolutionHours ?? 24));
  const [responseHours, setResponseHours] = useState(String(initial?.responseHours ?? ''));
  const [businessHoursOnly, setBHO] = useState(initial?.businessHoursOnly ?? false);
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const ok = await onSave({
      name, priority: priority || null, category: category || null,
      resolutionHours: Number(resolutionHours),
      responseHours: responseHours ? Number(responseHours) : null,
      businessHoursOnly, isDefault,
    });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <Modal open onClose={onClose} size="md" title={initial ? 'Edit SLA policy' : 'New SLA policy'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving || !name || !resolutionHours}>Save</Button></>}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Policy name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Select label="Applies to priority" options={PRIORITY_OPTS} value={priority} onChange={e => setPriority(e.target.value)} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category (optional)</label>
            <input value={category} onChange={e => setCategory(e.target.value)} placeholder="Any"
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Resolution (hours)</label>
            <input type="number" min={1} value={resolutionHours} onChange={e => setResolutionHours(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Response (hours, optional)</label>
            <input type="number" min={1} value={responseHours} onChange={e => setResponseHours(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={businessHoursOnly} onChange={e => setBHO(e.target.checked)} />
          Count business hours only (uses work hours/days from Settings)
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
          Default policy (used when none match)
        </label>
      </div>
    </Modal>
  );
}

export default function SlaPolicies() {
  const { policies, loading, create, update, remove } = useSlaPolicies();
  const [editing, setEditing] = useState<SlaPolicy | null>(null);
  const [creating, setCreating] = useState(false);

  const columns: Column<SlaPolicy>[] = [
    { key: 'name', header: 'Policy', sortValue: p => p.name, render: p => (
      <span className="flex items-center gap-2 font-medium text-navy">
        {p.name}{p.isDefault && <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Star size={11} /> default</span>}
      </span>
    ) },
    { key: 'priority', header: 'Priority', render: p => p.priority ?? <span className="text-gray-300">Any</span> },
    { key: 'category', header: 'Category', render: p => p.category ?? <span className="text-gray-300">Any</span> },
    { key: 'resolution', header: 'Resolution', sortValue: p => p.resolutionHours,
      render: p => <span className="inline-flex items-center gap-1"><Clock size={13} className="text-gray-400" />{p.resolutionHours}h</span> },
    { key: 'bh', header: 'Business hrs', render: p => p.businessHoursOnly ? 'Yes' : 'No' },
    { key: 'actions', header: '', align: 'right', render: p => (
      <span className="flex items-center justify-end gap-2">
        <button onClick={e => { e.stopPropagation(); setEditing(p); }} className="text-gray-300 hover:text-brand"><Pencil size={14} /></button>
        <button onClick={e => { e.stopPropagation(); remove(p.id); }} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
      </span>
    ) },
  ];

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-navy">SLA Policies</h1>
          <p className="text-sm text-gray-500">Resolution targets by priority/category. Business hours come from Settings.</p>
        </div>
        <Button onClick={() => setCreating(true)}><Plus size={15} /> New policy</Button>
      </div>

      <Card flush>
        {!loading && policies.length === 0 ? (
          <EmptyState icon={Clock} title="No SLA policies" description="Add one to set resolution targets (falls back to defaults otherwise)." />
        ) : (
          <DataTable columns={columns} data={policies} rowKey={p => p.id} loading={loading} pageSize={25} />
        )}
      </Card>

      {creating && <Editor onClose={() => setCreating(false)} onSave={create} />}
      {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSave={d => update(editing.id, d)} />}
    </div>
  );
}
